import { supabase } from './SupabaseConfig';
import { OrderRepository } from '../../domain/pos/ports/OrderRepository';
import type { OrderData, VoidLogData } from '@core/types';
import { InfrastructureError, NotFoundError } from '@core/errors/AppError';

export class SupabaseOrderRepository implements OrderRepository {
  async createOrder(uid: string, orderData: Partial<OrderData>): Promise<string> {
    try {
      const { data: order, error } = await supabase
        .from('orders')
        .insert({
          ...orderData,
          owner_id: uid,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return order.id;
    } catch (error) {
      throw new InfrastructureError('Lỗi tạo đơn hàng (Supabase)', 'Supabase', error as Error);
    }
  }

  async updateOrder(uid: string, orderId: string, updates: Partial<OrderData>): Promise<void> {
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId)
        .eq('owner_id', uid);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật đơn hàng (Supabase)', 'Supabase', error as Error);
    }
  }

  async updateOrderStatus(uid: string, orderId: string, status: string, extra?: Record<string, unknown>): Promise<void> {
    try {
      const payload: any = { 
        status, 
        updated_at: new Date().toISOString() 
      };
      if (extra) payload.metadata = extra;

      const { error } = await supabase
        .from('orders')
        .update(payload)
        .eq('id', orderId)
        .eq('owner_id', uid);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật trạng thái đơn hàng (Supabase)', 'Supabase', error as Error);
    }
  }

  private mapOrder(d: any): OrderData {
    if (!d) return null;
    return {
      ...d,
      createdAt: d.created_at ? new Date(d.created_at) : null,
      items: d.order_items || [],
      payments: d.payments || []
    } as any;
  }

  async getOrder(uid: string, orderId: string): Promise<OrderData | null> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*, order_items(*), payments(*)')
        .eq('id', orderId)
        .eq('owner_id', uid)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return this.mapOrder(data);
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy thông tin đơn hàng (Supabase)', 'Supabase', error as Error);
    }
  }

  async getRecentOrdersPaginated(uid: string, opts: any = {}): Promise<{ orders: OrderData[]; lastDoc: any }> {
    try {
      const limitCount = opts.limit || 50;
      let query = supabase
        .from('orders')
        .select('*, order_items(*), payments(*)')
        .eq('owner_id', uid)
        .order('created_at', { ascending: false })
        .limit(limitCount);

      if (opts.startAfterDoc) {
        query = query.lt('created_at', opts.startAfterDoc);
      }

      const { data, error } = await query;
      if (error) throw error;

      const orders = (data || []).map(d => this.mapOrder(d));
      const lastDoc = data && data.length === limitCount ? data[data.length - 1].created_at : null;

      return { orders, lastDoc };
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy danh sách đơn hàng (Supabase)', 'Supabase', error as Error);
    }
  }

  async getCompletedOrdersInRange(uid: string, startDate: Date, endDate: Date): Promise<OrderData[]> {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*, order_items(*), payments(*)')
        .eq('owner_id', uid)
        .eq('status', 'completed')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) throw error;
      return data as OrderData[];
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy báo cáo đơn hàng (Supabase)', 'Supabase', error as Error);
    }
  }

  listenPendingOrders(uid: string, callback: (orders: OrderData[]) => void): () => void {
    const channel = supabase
      .channel(`pending-orders-owner-${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `owner_id=eq.${uid},status=eq.pending`
        },
        async () => {
          const { data } = await supabase
            .from('orders')
            .select('*')
            .eq('owner_id', uid)
            .eq('status', 'pending');
          if (data) callback(data as OrderData[]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  listenConfirmedOrders(uid: string, callback: (orders: OrderData[]) => void): () => void {
    const channel = supabase
      .channel(`confirmed-orders-owner-${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `owner_id=eq.${uid},status=eq.confirmed`
        },
        async () => {
          const { data } = await supabase
            .from('orders')
            .select('*')
            .eq('owner_id', uid)
            .eq('status', 'confirmed');
          if (data) callback(data as OrderData[]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  listenKDSOrders(uid: string, callback: (orders: OrderData[]) => void, locationId?: string | null): () => void {
    let filter = `owner_id=eq.${uid},status=in.(confirmed,processing)`;
    if (locationId) filter += `,store_id=eq.${locationId}`;

    const channel = supabase
      .channel(`kds-orders-owner-${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter
        },
        async () => {
          let q = supabase
            .from('orders')
            .select('*, order_items(*)')
            .eq('owner_id', uid)
            .in('status', ['confirmed', 'processing']);
          
          if (locationId) q = q.eq('store_id', locationId);
          
          const { data } = await q;
          if (data) callback(data as OrderData[]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  listenDeliveryOrders(uid: string, callback: (orders: OrderData[]) => void, locationId?: string | null): () => void {
    let filter = `owner_id=eq.${uid},order_type=eq.delivery`;
    if (locationId) filter += `,store_id=eq.${locationId}`;

    const channel = supabase
      .channel(`delivery-orders-owner-${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter
        },
        async () => {
          let q = supabase
            .from('orders')
            .select('*')
            .eq('owner_id', uid)
            .eq('order_type', 'delivery');
          
          if (locationId) q = q.eq('store_id', locationId);
          
          const { data } = await q;
          if (data) callback(data as OrderData[]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  async logVoid(uid: string, payload: VoidLogData): Promise<void> {
    try {
      const { error } = await supabase
        .from('void_logs')
        .insert({
          ...payload,
          owner_id: uid,
          created_at: new Date().toISOString()
        });

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi lưu log hủy món (Supabase)', 'Supabase', error as Error);
    }
  }

  listenVoidLogs(uid: string, callback: (logs: VoidLogData[]) => void, opts: any = {}): () => void {
    const channel = supabase
      .channel(`void-logs-owner-${uid}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'void_logs',
          filter: `owner_id=eq.${uid}`
        },
        async () => {
          const { data } = await supabase
            .from('void_logs')
            .select('*')
            .eq('owner_id', uid)
            .order('created_at', { ascending: false })
            .limit(opts.limit || 50);
          if (data) callback(data as VoidLogData[]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  async getPublicMenu(uid: string): Promise<{ menuItems: unknown[]; brandName: string }> {
    try {
      const { data: menu, error: menuError } = await supabase
        .from('menus')
        .select('*, menu_items(*)')
        .eq('owner_id', uid)
        .eq('is_active', true)
        .maybeSingle();

      if (menuError) throw menuError;

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('business_name')
        .eq('id', uid)
        .single();

      if (profileError) throw profileError;

      return {
        menuItems: menu?.menu_items || [],
        brandName: profile.business_name
      };
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy menu công khai (Supabase)', 'Supabase', error as Error);
    }
  }

  listenMenu(uid: string, callback: (data: { menuItems: unknown[]; menuCategories: unknown[] }) => void): () => void {
    const channel = supabase
      .channel(`menu-owner-${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'menu_items',
          filter: `owner_id=eq.${uid}`
        },
        async () => {
          const { menuItems, brandName } = await this.getPublicMenu(uid);
          // Assuming categories are fetched as part of menu or separate table
          const { data: categories } = await supabase
            .from('menu_categories')
            .select('*')
            .eq('owner_id', uid);
          
          callback({ menuItems, menuCategories: categories || [] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }
}
